
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Stack,
  Paper,
  Chip,
  Alert,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';

const TEMPLATE_VARS = [
  { key: '{{guest.name}}', label: 'Guest name' },
  { key: '{{property.name}}', label: 'Property name' },
  { key: '{{room.number}}', label: 'Room number' },
];

function asItem(raw, idx) {
  if (!raw || typeof raw !== 'object') {
    return { id: `qr_${Date.now()}_${idx}`, title: '', text: '', order: idx };
  }
  return {
    id: raw.id || `qr_${Date.now()}_${idx}`,
    title: raw.title || '',
    text: raw.text || '',
    order: typeof raw.order === 'number' ? raw.order : idx,
    tags: Array.isArray(raw.tags) ? raw.tags : [],
  };
}

export default function QuickResponseSettings({
  initial = [],
  loading = false,
  onSave,
  onRefresh,
}) {
  const defaults = useMemo(
    () => (Array.isArray(initial) ? initial.map((item, idx) => asItem(item, idx)) : []),
    [initial]
  );

  const [items, setItems] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    setItems(defaults);
  }, [defaults]);

  const handleAdd = () => {
    const now = Date.now();
    setItems((prev) => [
      ...prev,
      {
        id: `qr_${now}`,
        title: 'New quick response',
        text: 'Hi {{guest.name}}, how can we help you today?',
        order: prev.length,
        tags: [],
      },
    ]);
  };

  const handleDelete = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleChange = (idx, key) => (event) => {
    const value = event?.target?.value ?? '';
    setItems((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, [key]: value } : item))
    );
  };

  const handleInsertVar = (idx, variable) => {
    if (!variable) return;
    setItems((prev) =>
      prev.map((item, i) =>
        i === idx
          ? {
              ...item,
              text: (item.text || '').concat(item.text ? ` ${variable}` : variable),
            }
          : item
      )
    );
  };

  const handleCancel = () => {
    setItems(defaults);
    setError('');
  };

  const sanitizedItems = () =>
    items.map((item, index) => ({
      id: item.id || `qr_${Date.now()}_${index}`,
      title: String(item.title || '').trim(),
      text: String(item.text || '').trim(),
      order: index,
      tags: Array.isArray(item.tags) ? item.tags.filter(Boolean) : [],
    }));

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const payload = sanitizedItems();
      for (const item of payload) {
        if (!item.title) {
          throw new Error('Each quick response needs a title.');
        }
        if (!item.text) {
          throw new Error('Each quick response needs message text.');
        }
      }
      if (onSave) {
        const res = await onSave(payload);
        if (res && res.success === false && res.error) {
          throw new Error(res.error);
        }
      }
      setSavedAt(new Date());
    } catch (err) {
      setError(err?.message || 'Failed to save quick responses.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">⚡ Quick Responses</Typography>
        {loading && <CircularProgress size={18} thickness={5} />}
        <Tooltip title="Refresh">
          <span>
            <IconButton
              aria-label="Refresh quick responses"
              size="small"
              onClick={onRefresh ? () => onRefresh() : undefined}
              disabled={loading || saving || !onRefresh}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage reusable replies your team can send with one click. Use template variables to
        personalize messages—for example, add the guest&apos;s name with <code>{'{{guest.name}}'}</code>.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {savedAt && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Saved at {savedAt.toLocaleTimeString()}
        </Alert>
      )}

      <Stack spacing={2} sx={{ mb: 3 }}>
        {items.length === 0 && !loading && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            No quick responses yet. Add your first template below.
          </Paper>
        )}
        {items.map((item, idx) => (
          <Paper key={item.id || idx} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Quick response #{idx + 1}</Typography>
              <IconButton
                aria-label="Delete quick response"
                onClick={() => handleDelete(idx)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Stack spacing={1.5}>
              <TextField
                label="Title"
                value={item.title}
                onChange={handleChange(idx, 'title')}
                placeholder="Welcome message"
                fullWidth
              />
              <TextField
                label="Message"
                value={item.text}
                onChange={handleChange(idx, 'text')}
                placeholder="Hello {{guest.name}}, welcome to our property!"
                multiline
                minRows={3}
                fullWidth
              />
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {TEMPLATE_VARS.map((variable) => (
                  <Chip
                    key={variable.key}
                    label={variable.label}
                    size="small"
                    onClick={() => handleInsertVar(idx, variable.key)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAdd}
        sx={{ mb: 4 }}
      >
        Add quick response
      </Button>

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </Box>
  );
}
